import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";

const Sociedade = () => {
  const { t } = useLang();

  return (
    <Layout>
      {/* Intro */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24" data-testid="sociedade-intro">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#666]">{t.sociedade.eyebrow}</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-light tracking-tight text-4xl sm:text-5xl md:text-6xl max-w-4xl leading-[1.1]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="sociedade-title"
          >
            {t.sociedade.title}
          </motion.h1>
          <div className="mt-10 grid md:grid-cols-2 gap-10 max-w-5xl">
            <p className="text-lg md:text-xl text-[#333] leading-relaxed">{t.sociedade.lead}</p>
            <p className="text-lg md:text-xl text-[#333] leading-relaxed">{t.sociedade.leadExtra}</p>
          </div>
        </div>
      </section>

      {/* Origem — Cátia & João */}
      <section className="py-24 md:py-32 bg-[#EBEBE6]" data-testid="sociedade-origin">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#666]">{t.sociedade.originTitle}</span>
          <p
            className="mt-4 max-w-3xl font-light tracking-tight text-2xl md:text-3xl leading-[1.3]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.sociedade.originIntro}
          </p>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-[#D5D1C9]">
            {[
              {
                name: t.sociedade.founder1Name,
                role: t.sociedade.founder1Role,
                bio: t.sociedade.founder1Bio,
                num: "01",
              },
              {
                name: t.sociedade.founder2Name,
                role: t.sociedade.founder2Role,
                bio: t.sociedade.founder2Bio,
                num: "02",
              },
            ].map((f) => (
              <motion.div
                key={f.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-[#EBEBE6] p-8 md:p-12 hover:bg-[#F0ECE4] transition-colors duration-500"
                data-testid={`founder-${f.num}`}
              >
                <p className="text-5xl font-light text-[#1C1C1C]/20" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {f.num}
                </p>
                <h3 className="mt-8 text-3xl md:text-4xl font-light tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {f.name}
                </h3>
                <p className="mt-2 uppercase tracking-[0.22em] text-xs text-[#666]">{f.role}</p>
                <p className="mt-6 text-[#333] leading-relaxed text-base">{f.bio}</p>
              </motion.div>
            ))}
          </div>

          <p className="mt-16 max-w-3xl text-lg md:text-xl text-[#333] leading-relaxed">
            {t.sociedade.originClose}
          </p>
        </div>
      </section>

      {/* Conceito Bola 8 */}
      <section className="py-24 md:py-36 relative overflow-hidden" data-testid="sociedade-concept">
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <img
            src="/logo_bola8.png"
            alt=""
            className="w-[90%] md:w-[70%] max-w-[900px] opacity-[0.06] select-none"
          />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#666]">Bola 8</span>
          <h2
            className="mt-4 font-light tracking-tight text-3xl md:text-5xl"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.sociedade.conceptTitle}
          </h2>
          <p className="mt-10 text-lg md:text-xl text-[#333] leading-relaxed">
            {t.sociedade.conceptIntro}
          </p>

          <ul className="mt-8 space-y-4">
            {[t.sociedade.c1, t.sociedade.c2, t.sociedade.c3].map((c, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-start gap-5 border-b border-[#1C1C1C]/15 pb-4"
              >
                <span className="text-xs uppercase tracking-[0.22em] text-[#666] pt-1.5 w-10 shrink-0">
                  0{i + 1}
                </span>
                <span className="text-xl md:text-2xl font-light tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {c}
                </span>
              </motion.li>
            ))}
          </ul>

          <p className="mt-12 max-w-3xl text-lg md:text-xl text-[#333] leading-relaxed italic">
            {t.sociedade.conceptClose}
          </p>
        </div>
      </section>

      {/* O Que Nos Distingue */}
      <section className="py-24 md:py-32 bg-[#1C1C1C] text-white" data-testid="sociedade-distinguish">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-white/50">{t.sociedade.distinguishTitle}</span>
          <h2
            className="mt-4 font-light tracking-tight text-3xl md:text-5xl max-w-3xl"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.sociedade.distinguishTitle}
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10">
            {[t.sociedade.d1, t.sociedade.d2, t.sociedade.d3, t.sociedade.d4].map((d, i) => (
              <div key={i} className="bg-[#1C1C1C] p-8 md:p-10 hover:bg-[#222] transition-colors duration-500">
                <p className="text-5xl font-light text-white/20" style={{ fontFamily: "Outfit, sans-serif" }}>
                  0{i + 1}
                </p>
                <p className="mt-8 text-xl md:text-2xl font-light tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visão */}
      <section className="py-24 md:py-32 bg-[#D4C9B8]" data-testid="sociedade-vision">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.28em] text-xs text-[#5a5143]">{t.sociedade.visionTitle}</span>
          <p
            className="mt-8 font-light tracking-tight text-2xl md:text-4xl leading-[1.25] text-[#1C1C1C]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.sociedade.visionText}
          </p>
          <Link
            to="/contactos"
            data-testid="sociedade-cta-btn"
            className="mt-12 inline-flex items-center gap-3 bg-[#1C1C1C] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] hover:bg-black transition"
          >
            {t.nav.cta}
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Sociedade;
